import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import SectionSubtitle from "../SectionSubtitle";
import MonthYearSelector from "./MonthYearSelector";

type BillablePeriodCardProps = {
  calendarSubscriptionStartDay?: number;
  setDateRange: (dateRange: { startDate: string; endDate: string }) => void;
};

const BillablePeriodCard = ({
  calendarSubscriptionStartDay,
  setDateRange,
}: BillablePeriodCardProps) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentDayOfMonth = currentDate.getDate();

  // Determine initial month based on subscription start day
  const initialMonth =
    calendarSubscriptionStartDay && currentDayOfMonth < calendarSubscriptionStartDay
      ? currentMonth - 1
      : currentMonth;

  // Handle case where we need to go to previous year
  const initialYear = initialMonth < 0 ? currentYear - 1 : currentYear;
  const adjustedInitialMonth = initialMonth < 0 ? 11 : initialMonth;

  const [localViewYear, setLocalViewYear] = useState(initialYear);
  const [localViewMonth, setLocalViewMonth] = useState(adjustedInitialMonth);

  // Update local view when the subscription day changes
  useEffect(() => {
    const newInitialMonth =
      calendarSubscriptionStartDay && currentDayOfMonth < calendarSubscriptionStartDay
        ? currentMonth - 1
        : currentMonth;

    const newInitialYear = newInitialMonth < 0 ? currentYear - 1 : currentYear;
    const newAdjustedInitialMonth = newInitialMonth < 0 ? 11 : newInitialMonth;

    setLocalViewYear(newInitialYear);
    setLocalViewMonth(newAdjustedInitialMonth);
  }, [calendarSubscriptionStartDay, currentDayOfMonth, currentMonth, currentYear]);

  const dateRangeRef = useRef<{ startDate: string | null; endDate: string | null }>({
    startDate: null,
    endDate: null,
  });

  const getBillingCycleDates = () => {
    if (!calendarSubscriptionStartDay) {
      return { startDate: null, endDate: null };
    }

    const startDate = new Date(localViewYear, localViewMonth, calendarSubscriptionStartDay);
    const endDate = new Date(localViewYear, localViewMonth + 1, calendarSubscriptionStartDay - 1);

    return { startDate, endDate };
  };

  const { startDate, endDate } = getBillingCycleDates();
  const selectedDate = new Date(localViewYear, localViewMonth, 1);

  const formatDateForApi = (date: Date | null): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const formattedStartDate = formatDateForApi(startDate);
    const formattedEndDate = formatDateForApi(endDate);

    if (
      formattedStartDate !== dateRangeRef.current.startDate ||
      formattedEndDate !== dateRangeRef.current.endDate
    ) {
      if (formattedStartDate && formattedEndDate) {
        dateRangeRef.current = {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        };

        setDateRange({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        });
      }
    }
  }, [startDate, endDate, setDateRange]);

  const formatDate = (date: Date, format: string) => {
    const months = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    const fullMonths = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    if (format === "MMMM YYYY") {
      return `${fullMonths[month]} ${year}`;
    }

    return `${day < 10 ? "0" + day : day} ${months[month]}, ${year}`;
  };

  const handleMonthSelect = (monthIndex: number) => {
    if (
      localViewYear > currentYear ||
      (localViewYear === currentYear && monthIndex > currentMonth)
    ) {
      return;
    }

    setLocalViewMonth(monthIndex);
  };

  const handleViewChange = (year: number, month: number) => {
    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      return;
    }

    setLocalViewYear(year);
    setLocalViewMonth(month);
  };

  return (
    <div className="h-full rounded-lg border bg-card shadow-md">
      <div className="p-6">
        <SectionSubtitle subtitle="Período de Facturación" />

        <div className="mb-4 mt-4 flex items-center justify-between">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="default">
                {formatDate(selectedDate, "MMMM YYYY")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-4" align="start">
              <MonthYearSelector
                selectedMonth={selectedDate.getMonth()}
                selectedYear={selectedDate.getFullYear()}
                viewMonth={localViewMonth}
                viewYear={localViewYear}
                onMonthSelect={handleMonthSelect}
                onViewChange={handleViewChange}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="mt-4 flex flex-col">
          <div className="mb-2 flex justify-between">
            <span className="font-medium text-gray-secondary">Fecha de inicio:</span>
            {startDate ? (
              <span className="font-bold">{formatDate(startDate, "MMM DD, YYYY")}</span>
            ) : (
              <Skeleton className="h-5 w-[100px]" />
            )}
          </div>

          <div className="flex justify-between">
            <span className="font-medium text-gray-secondary">Fecha de fin:</span>
            {endDate ? (
              <span className="font-bold">{formatDate(endDate, "MMM DD, YYYY")}</span>
            ) : (
              <Skeleton className="h-5 w-[100px]" />
            )}
          </div>

          {calendarSubscriptionStartDay && (
            <div className="mt-4 rounded-md bg-gray-tertiary p-4">
              <p className="font-medium text-palomma-secondary">
                La factura se genera el día {calendarSubscriptionStartDay} de cada mes.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillablePeriodCard;
